import { motion } from "framer-motion";
import { Check, X, ArrowRight } from "lucide-react";
import { openInquiry } from "@/lib/scroll";
import CAMPAIGN from "@/data/campaign";

const EASE = [0.76, 0, 0.24, 1];

const Inclusions = () => {
  const p = CAMPAIGN.pricing;

  return (
    <section id="included" className="py-24 md:py-40" data-testid="inclusions-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: EASE }}
              className="relative"
            >
              <img
                src={p.image}
                alt={p.imageAlt}
                className="w-full h-[380px] md:h-[480px] object-cover"
                data-testid="inclusions-image"
              />
              <div className="absolute -bottom-8 -right-2 md:-right-8 bg-primary text-primary-foreground p-8 md:p-10 max-w-[280px]" data-testid="price-anchor-card">
                <p className="uppercase text-[10px] tracking-[0.25em] font-semibold text-primary-foreground/70 mb-2">
                  {p.label}
                </p>
                <p className="font-heading font-light text-5xl md:text-6xl leading-none">{p.price}</p>
                <p className="mt-3 text-xs font-light text-primary-foreground/80 leading-relaxed">
                  {p.note}
                </p>
                <button
                  onClick={openInquiry}
                  className="mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold border-b border-primary-foreground/40 pb-1 hover:gap-4 transition-all duration-300"
                  data-testid="price-cta-button"
                >
                  Get my quote <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-7 lg:pt-8">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: EASE }}
              className="uppercase text-xs tracking-[0.25em] font-semibold text-primary mb-6"
              data-testid="inclusions-overline"
            >
              {p.overline}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
              className="font-heading font-light tracking-tight text-foreground text-4xl sm:text-5xl leading-[1.05] mb-12"
              data-testid="inclusions-heading"
            >
              {p.headingPlain}<br /><em className="text-primary">{p.headingAccent}</em>
            </motion.h2>

            <div className="grid sm:grid-cols-2 gap-0 border-t border-l border-border">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: EASE }}
                className="border-r border-b border-border p-8"
                data-testid="included-list"
              >
                <h3 className="font-heading text-2xl font-medium text-foreground mb-6">Included</h3>
                <ul className="space-y-4">
                  {p.included.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-light text-foreground/80">
                      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.12 }}
                className="border-r border-b border-border p-8 bg-secondary/40"
                data-testid="excluded-list"
              >
                <h3 className="font-heading text-2xl font-medium text-foreground mb-6">Not included</h3>
                <ul className="space-y-4">
                  {p.excluded.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-light text-foreground/70">
                      <X className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Inclusions;
